import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { ThemeToggle } from "../components/ThemeToggle";
import api from "../api/axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [status, setStatus] = useState("form"); // form, loading, success, error
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage(t.invalidOrExpiredToken);
    }
  }, [token, t]);

  // Şifre validasyon fonksiyonu
  const validatePassword = (pwd) => {
    setPasswordValidation({
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':\"\\|,.<>/?]/.test(pwd),
    });
  };

  const handlePasswordChange = (e) => {
    const newPwd = e.target.value;
    setNewPassword(newPwd);
    validatePassword(newPwd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error(t.invalidOrExpiredToken);
      return;
    }

    // Validate passwords
    if (!newPassword || !confirmPassword) {
      toast.error(
        t.allFieldsRequired || "Please fill in all fields"
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(
        language === "tr"
          ? "Şifreler eşleşmiyor"
          : "Passwords do not match"
      );
      return;
    }

    const isPasswordValid = Object.values(passwordValidation).every(
      (rule) => rule === true
    );

    if (!isPasswordValid) {
      toast.error(
        t.passwordRequirementsNotMet ||
          "Please meet the password requirements"
      );
      return;
    }

    setLoading(true);
    setStatus("loading");

    try {
      const response = await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
      });

      setStatus("success");
      setMessage(response.data.message || t.passwordResetSuccess);
      toast.success(t.passwordResetSuccess);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      setStatus("error");
      let errorMessage = t.passwordResetFailed;

      if (error.response?.status === 400) {
        setMessage(
          error.response.data.detail || t.invalidOrExpiredToken
        );
      } else {
        setMessage(
          error.response?.data?.detail || t.passwordResetFailed
        );
      }

      toast.error(errorMessage);
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reset Password - FeedPanels</title>
        <meta name="description" content="Set a new password for your FeedPanels account. Use the link from your email to complete the reset securely." />
        <link rel="canonical" href="https://feedpanels.com/reset-password" />
      </Helmet>
      <div className="min-h-screen auth-gradient dark:bg-gray-900 flex items-center justify-center p-4 relative">
      {/* Theme Toggle - Top right corner */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-600 text-white rounded-2xl mb-4">
            <Lock size={32} />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            FeedPanels
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Reset your password</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t.resetPasswordTitle || "Reset Password"}</CardTitle>
            <CardDescription>
              {status === "form"
                ? t.enterNewPassword || "Enter your new password"
                : status === "success"
                ? t.redirectingToLogin
                : status === "error"
                ? t.passwordResetFailed
                : t.resettingPassword}
            </CardDescription>
          </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {status === "form" && (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    {t.newPassword || "New Password"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength={8}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <Eye size={18} />
                      ) : (
                        <EyeOff size={18} />
                      )}
                    </button>
                  </div>

                  {/* Password Validation Messages */}
                  {newPassword && (
                    <div className="mt-2 space-y-1.5 text-sm">
                      <div
                        className={`flex items-center gap-2 ${
                          passwordValidation.minLength
                            ? "text-green-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        <span className="text-xs">
                          {passwordValidation.minLength ? "✓" : "✗"}
                        </span>
                        <span>{t.minLength || "At least 8 characters"}</span>
                      </div>
                      <div
                        className={`flex items-center gap-2 ${
                          passwordValidation.hasUpperCase
                            ? "text-green-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        <span className="text-xs">
                          {passwordValidation.hasUpperCase ? "✓" : "✗"}
                        </span>
                        <span>
                          {t.hasUpperCase ||
                            "At least one uppercase letter"}
                        </span>
                      </div>
                      <div
                        className={`flex items-center gap-2 ${
                          passwordValidation.hasLowerCase
                            ? "text-green-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        <span className="text-xs">
                          {passwordValidation.hasLowerCase ? "✓" : "✗"}
                        </span>
                        <span>
                          {t.hasLowerCase ||
                            "At least one lowercase letter"}
                        </span>
                      </div>
                      <div
                        className={`flex items-center gap-2 ${
                          passwordValidation.hasNumber
                            ? "text-green-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        <span className="text-xs">
                          {passwordValidation.hasNumber ? "✓" : "✗"}
                        </span>
                        <span>{t.hasNumber || "At least one number"}</span>
                      </div>
                      <div
                        className={`flex items-center gap-2 ${
                          passwordValidation.hasSpecialChar
                            ? "text-green-400"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      >
                        <span className="text-xs">
                          {passwordValidation.hasSpecialChar ? "✓" : "✗"}
                        </span>
                        <span>
                          {t.hasSpecialChar ||
                            "At least one special character"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    {t.confirmPassword || "Confirm Password"}
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                      aria-label={
                        showConfirmPassword
                          ? "Hide password"
                          : "Show password"
                      }
                    >
                      {showConfirmPassword ? (
                        <Eye size={18} />
                      ) : (
                        <EyeOff size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                  disabled={loading}
                >
                  {loading
                    ? t.resettingPassword || "Resetting password..."
                    : t.resetPassword || "Reset Password"}
                </Button>
              </form>
            )}

            {status === "success" && (
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 rounded-full mb-4">
                  <CheckCircle2 size={32} />
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {t.redirectingToLogin}
                </p>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{message}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate("/forgot-password")}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    {t.requestNewLink || "Request New Link"}
                  </Button>

                  <Button
                    onClick={() => navigate("/login")}
                    variant="outline"
                    className="flex-1"
                  >
                    {t.signIn || "Sign In"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
    </>
  );
};
